/**
 * API Client Service
 * 
 * This service provides functions for interacting with the backend API.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://9o8xzpssal.execute-api.us-east-1.amazonaws.com';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Get all users with optional filtering
 * @param {Object} filters - Filters to apply (role, status, etc.)
 * @returns {Promise<Object>} - Object containing users array and total count
 */
const getUsers = async (filters = {}) => {
  // Build query string from filters
  const queryParams = new URLSearchParams();
  
  if (filters.role) queryParams.append('role', filters.role);
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
  if (filters.sortDirection) queryParams.append('sortDirection', filters.sortDirection);
  if (filters.limit) queryParams.append('limit', filters.limit);
  if (filters.offset) queryParams.append('offset', filters.offset);
  
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/users${queryString ? `?${queryString}` : ''}`;
  
  const response = await fetch(url, { headers: authHeaders() });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch users');
  }
  
  return await response.json();
};

/**
 * Get user by ID
 * @param {number} userId - User ID
 * @returns {Promise<Object>} - User object
 */
const getUserById = async (userId) => {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, { headers: authHeaders() });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user');
  }
  
  return await response.json();
};

/**
 * Register a new user
 * @param {Object} userData - User data
 * @param {string} role - Role to assign to the user
 * @returns {Promise<Object>} - Created user object
 */
const registerUser = async (userData, role) => {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ userData, role })
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create user');
  }
  
  return await response.json();
};

/**
 * Update user
 * @param {number} userId - User ID
 * @param {Object} userData - User data to update
 * @returns {Promise<Object>} - Updated user object
 */
const updateUser = async (userId, userData) => {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(userData)
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update user');
  }
  
  return await response.json();
};

/**
 * Delete user
 * @param {number} userId - User ID
 * @returns {Promise<boolean>} - Success status
 */
const deleteUser = async (userId) => {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  
  if (!response.ok && response.status !== 204) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete user');
  }
  
  return true;
};

/**
 * Authenticate a user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} - User object with roles and permissions
 */
const authenticateUser = async (email, password) => {
  const response = await fetch(`${API_BASE_URL}/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Authentication failed');
  }

  const data = await response.json();
  if (data.token) {
    localStorage.setItem('authToken', data.token);
  }
  return data;
};

const getApplications = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.grade) params.append('grade', filters.grade);
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/applications${qs ? `?${qs}` : ''}`, {
    headers: authHeaders()
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch applications');
  }
  return await response.json();
};

const getApplicationById = async (id) => {
  const response = await fetch(`${API_BASE_URL}/applications/${id}`, { headers: authHeaders() });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch application');
  }
  return await response.json();
};

const updateApplicationStatus = async (id, status, reviewedBy) => {
  const response = await fetch(`${API_BASE_URL}/applications/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ status, reviewed_by: reviewedBy })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update application status');
  }
  return await response.json();
};

const submitApplication = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData)
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to submit application');
  }
  return await response.json();
};

const createProspect = async (prospectData) => {
  const response = await fetch(`${API_BASE_URL}/prospects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prospectData)
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to submit request');
  }
  return await response.json();
};

const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to send reset email');
  }
  return await response.json();
};

const resetPassword = async (token, newPassword) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to reset password');
  }
  return await response.json();
};

const changePassword = async (userId, currentPassword, newPassword) => {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ _action: 'changePassword', currentPassword, newPassword })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to change password');
  }
  return await response.json();
};

const unlockUser = async (userId) => {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ _action: 'unlock' })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to unlock account');
  }
  return await response.json();
};

const getStudentParents = async (studentUserId) => {
  const response = await fetch(`${API_BASE_URL}/students/${studentUserId}/parents`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to fetch student parents');
  return response.json();
};

const linkParentToStudent = async (studentUserId, payload) => {
  const response = await fetch(`${API_BASE_URL}/students/${studentUserId}/parents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || 'Failed to link parent');
  }
  return response.json();
};

const unlinkParentFromStudent = async (studentUserId, parentUserId) => {
  const response = await fetch(`${API_BASE_URL}/students/${studentUserId}/parents/${parentUserId}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to unlink parent');
  }
};

const getDbStats = async () => {
  const response = await fetch(`${API_BASE_URL}/admin/db-stats`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to fetch DB stats');
  return response.json();
};

const getAuditLog = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.action) params.append('action', filters.action);
  if (filters.actorEmail) params.append('actorEmail', filters.actorEmail);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/admin/audit-log${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to fetch audit log');
  return response.json();
};

const bulkOperation = async (operation, params, preview = false) => {
  const response = await fetch(`${API_BASE_URL}/admin/bulk-operation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ operation, params, preview })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Bulk operation failed');
  }
  return response.json();
};

const getSystemConfig = async () => {
  const response = await fetch(`${API_BASE_URL}/admin/system-config`, { headers: authHeaders() });
  if (!response.ok) throw new Error('Failed to fetch system config');
  return response.json();
};

const getDocumentUploadUrl = async (fileName, contentType, category) => {
  const response = await fetch(`${API_BASE_URL}/documents/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ fileName, contentType, category })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to get upload URL');
  }
  return response.json();
};

const getDocuments = async (role) => {
  const qs = role ? `?role=${role}` : '';
  const response = await fetch(`${API_BASE_URL}/documents${qs}`);
  if (!response.ok) throw new Error('Failed to fetch documents');
  return response.json();
};

const createDocument = async (metadata) => {
  const response = await fetch(`${API_BASE_URL}/documents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(metadata)
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to save document');
  }
  return response.json();
};

const updateDocument = async (documentId, updates) => {
  const response = await fetch(`${API_BASE_URL}/documents/${documentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(updates)
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update document');
  }
  return response.json();
};

const deleteDocument = async (documentId) => {
  const response = await fetch(`${API_BASE_URL}/documents/${documentId}`, {
    method: 'DELETE',
    headers: authHeaders()
  });
  if (!response.ok && response.status !== 204) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete document');
  }
};

const updateSystemConfig = async (key, value) => {
  const response = await fetch(`${API_BASE_URL}/admin/system-config/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ value })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update config');
  }
  return response.json();
};

export default {
  getUsers,
  getUserById,
  registerUser,
  updateUser,
  deleteUser,
  authenticateUser,
  submitApplication,
  createProspect,
  getApplications,
  getApplicationById,
  updateApplicationStatus,
  requestPasswordReset,
  resetPassword,
  changePassword,
  unlockUser,
  getStudentParents,
  linkParentToStudent,
  unlinkParentFromStudent,
  getDbStats,
  getAuditLog,
  bulkOperation,
  getSystemConfig,
  updateSystemConfig,
  getDocumentUploadUrl,
  getDocuments,
  createDocument,
  updateDocument,
  deleteDocument
};